/**
 * Stable organization-permission slugs for `checkPermission(getPermissions(), slug)`
 * and Strapi `organization-permission.slug`. Align with CMS catalog / seed migration.
 *
 * All slug string values are **snake_case** (lowercase, words separated by `_`).
 */
export const ORGANIZATION_PERMISSION_SLUGS = Object.freeze({
    /** Event details, settings, offers, seat map, unified search, etc. */
    EVENT_MANAGEMENT: 'create_manage_events',
    /** View events */
    VIEW_EVENTS: 'view_events',
    /** View season packages */
    VIEW_SEASON_PACKAGES: 'view_season_packages',
    /** Season packages management */
    SEASON_PACKAGES_MANAGEMENT: 'create_manage_season_packages',
    /** View flex packages */
    VIEW_FLEX_PACKAGES: 'view_flex_packages',
    /** Flex packages management */
    FLEX_PACKAGES_MANAGEMENT: 'create_manage_flex_packages',
    /** Balance & Payouts */
    VIEW_BALANCE_PAYOUTS: 'view_balance_payouts',
    /** Tax status */
    TAX_STATUS: 'tax_status',
    /** Guest list */
    GUEST_LIST: 'guest_list',
    /** Attendees */
    ATTENDEES: 'attendees',
    /** Create and manage tracking links */
    MANAGE_TRACKING_LINKS: 'create_manage_tracking_links',
    /** Contact attendees */
    CONTACT_ATTENDEES: 'contact_attendees',
    /** Primary and resale ticket management */
    TICKET_SALES: 'ticket_sales',
    /** View orders */
    VIEW_ORDERS: 'view_orders',
    /** Orders management */
    ORDERS_MANAGEMENT: 'manage_orders',
    /** View Access Control Summary */
    VIEW_ACCESS_CONTROL_SUMMARY: 'view_access_control_summary',
    /** View A/R Aging Details Report */
    VIEW_AR_AGING_DETAILS_REPORT: 'view_ar_aging_details_report',
    /** View A/R Aging Summary Report */
    VIEW_AR_AGING_SUMMARY_REPORT: 'view_ar_aging_summary_report',
    /** View Custom Fee Report */
    VIEW_CUSTOM_FEE_REPORT: 'view_custom_fee_report',
    /** View Daily Settlement Report */
    VIEW_DAILY_SETTLEMENT_REPORT: 'view_daily_settlement_report',
    /** View Daily Ticket Counts */
    VIEW_DAILY_TICKET_COUNTS: 'view_daily_ticket_counts',
    /** View Event Sales & Attendance */
    VIEW_EVENT_SALES_AND_ATTENDANCE: 'view_event_sales_attendance',
    /** View Order Listings */
    VIEW_ORDER_LISTINGS: 'view_order_listings',
    /** View Time Stamp Report */
    VIEW_TIME_STAMP_REPORT: 'view_time_stamp_report',
    /** Custom report builder */
    VIEW_REPORT_BUILDER: 'view_report_builder',
    /** Promo Codes */
    MANAGE_PROMO_CODES: 'create_manage_promo_codes',
    /** Fundraising campaigns */
    MANAGE_FUNDRAISING: 'create_manage_fundraising',
    /** Terminals */
    MANAGE_TERMINALS: 'create_manage_terminals',
    /** Scanners & entry points */
    MANAGE_SCANNERS: 'create_manage_scanners',
    /** Custom fees */
    MANAGE_CUSTOM_FEES: 'create_manage_custom_fees',
    /** Buyer types */
    MANAGE_BUYER_TYPES: 'create_manage_buyer_types',
    /** Food & beverage marketplace */
    MANAGE_MARKETPLACE: 'create_manage_marketplace',
    /** Complimentary ticket intelligence overview */
    VIEW_COMP_INTELLIGENCE: 'view_comp_intelligence',
    /** Unmasked comp recipient history */
    VIEW_COMP_INTELLIGENCE_RECIPIENTS: 'view_comp_intelligence_recipients',
    /** Export complimentary intelligence */
    EXPORT_COMP_INTELLIGENCE: 'export_comp_intelligence',
});
/**
 * Permission slugs accepted by Strapi `global::view-reports` for report routes in
 * `custom-report.js` (and `/analytic/daily-ticket-counts`). Having any one of
 * these should match a report the user can open in admin (see each report wrapper).
 */
export const ORGANIZATION_REPORT_API_PERMISSION_SLUGS = Object.freeze([
    ORGANIZATION_PERMISSION_SLUGS.VIEW_ACCESS_CONTROL_SUMMARY,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_AR_AGING_DETAILS_REPORT,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_AR_AGING_SUMMARY_REPORT,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_CUSTOM_FEE_REPORT,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_SETTLEMENT_REPORT,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_TICKET_COUNTS,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_EVENT_SALES_AND_ATTENDANCE,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_ORDER_LISTINGS,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_TIME_STAMP_REPORT,
    ORGANIZATION_PERMISSION_SLUGS.VIEW_REPORT_BUILDER,
]);
